<template>
  <div id="app">
    <NavBar v-show="showNavBar" />
    <RouterView :key="$route.fullPath" />
    <SessionExpiredModal :show="showSessionExpiredModal" @confirm="handleSessionExpiredConfirm" />
    <PasswordChangeReminderModal
      :show="showPasswordReminder"
      @changed="handlePasswordReminderResolved"
      @snoozed="handlePasswordReminderResolved"
    />
  </div>
</template>

<script>
import NavBar from "@/components/NavBar";
import SessionExpiredModal from "@/components/SessionExpiredModal";
import PasswordChangeReminderModal from "@/components/PasswordChangeReminderModal";
import { mapMutations } from "vuex";

export default {
  name: "App",
  components: { NavBar, SessionExpiredModal, PasswordChangeReminderModal },
  methods: {
    ...mapMutations("user", ["setUserInfo", "clearPasswordReminder"]),
    handlePasswordReminderResolved() {
      // 변경/스누즈 성공 시 로컬 flag 해제 → 모달 닫힘
      this.clearPasswordReminder();
    },
    // ...mapMutations("info", ["getServerInfo", "socket"]),
    ...mapMutations("info", ["getServerInfo"]),
    toAuthPage() {
      this.$router.push({
        name: "authentication"
      });
    },
    handleSessionExpiredEvent() {
      // 이미 처리 중이거나 모달이 표시된 상태면 무시
      if (this.isProcessingSessionExpired || this.showSessionExpiredModal) {
        return
      }
      
      // 로그인 페이지에서는 모달 표시하지 않음
      if (this.$route.name === 'authentication') {
        return
      }
      
      // 로그인 직후 5초 동안은 세션 만료 이벤트 무시 (세션 설정 중일 수 있음)
      const LOGIN_GRACE_PERIOD = 5000 // 5초
      if (this.lastLoginTime) {
        const timeSinceLogin = Date.now() - this.lastLoginTime
        if (timeSinceLogin < LOGIN_GRACE_PERIOD) {
          return
      }
      }
      
      // 세션 만료 이벤트는 인증된 상태에서만 발생하므로,
      // 인증 대기 중이 아닐 때 모달 표시
      // (이벤트 발생 시점에 이미 인증 상태가 변경되었을 수 있으므로,
      //  인증 대기 중이 아니면 표시)
      if (!this.isAuthenticatePending) {
        this.isProcessingSessionExpired = true
        this.showSessionExpiredModal = true
      }
    },
    handleSessionExpiredConfirm() {
      this.showSessionExpiredModal = false
      this.isProcessingSessionExpired = false
      
      // 이미 로그인 페이지가 아니면 리다이렉트
      if (this.$route.name !== 'authentication') {
        this.$router.push({
          name: "authentication"
        }).catch(() => {
          // 네비게이션 중복 에러 무시
        })
      }
    }
  },
  data() {
    return { 
      loader: null,
      showSessionExpiredModal: false,
      sessionExpiredHandler: null,
      isProcessingSessionExpired: false, // 세션 만료 처리 중 플래그
      lastLoginTime: null // 로그인 시점 기록
    };
  },
  computed: {
    showNavBar() {
      let notShow = ["authentication", "setup"];
      return notShow.indexOf(this.$route.name) === -1;
    },
    isAuthenticated() {
      return this.$store.state.user.isAuthenticated;
    },
    isAuthenticatePending() {
      return this.$store.state.user.isAuthenticatePending;
    },
    loginRequired() {
      if (this.isAuthenticatePending) {
        return false;
      }
      return !this.isAuthenticated;
    },
    showPasswordReminder() {
      if (!this.isAuthenticated) return false;
      if (this.$route.name === "authentication" || this.$route.name === "setup") return false;
      let user = this.$store.state.user.user;
      return (user && user.password_change_recommended) || false;
    },
    loading() {
      return this.$store.state.info.loading;
    },
    // socketConnection() {
    //   return true
    //   // return this.$store.state.info.socket;
    // }
  },
  watch: {
    loading() {
      if (!this.loading && this.loader != null) {
        this.loader.hide();
      }
    },
    isAuthenticated(newValue) {
      // 로그인 성공 시 세션 만료 플래그 리셋 및 로그인 시점 기록
      if (newValue) {
        this.isProcessingSessionExpired = false
        this.showSessionExpiredModal = false
        this.lastLoginTime = Date.now()
      } else {
        // 로그아웃 시 로그인 시점 초기화
        this.lastLoginTime = null
      }
    },
    // socketConnection() {
    //   if (this.socketConnection) return;

    //   setTimeout(() => {
    //     if (this.socketConnection) return;
    //     let options = {
    //       positionClass: "toast-bottom-left"
    //     };

    //     this.$toastr.warning(
    //       "Connection lost to the backend",
    //       "Connection Lost",
    //       options
    //     );
    //   }, 1000);
    // },
    loginRequired: {
      handler(newValue) {
        if (newValue) {
          this.toAuthPage();
        } else {
          if (this.$router.name == "authentication") {
            this.$router.push({
              name: "datasets"
            });
          }
        }
      },
      immediate: true
    }
  },
  // sockets: {
  //   connect() {
  //     this.socket(true);
  //   },
  //   disconnect() {
  //     this.socket(false);
  //   }
  // },
  mounted() {
    if (this.$route.name.toLowerCase() !== "annotate") {
      this.loader = this.$loading.show({
        height: 100
      });
    }
    
    // Listen for session expired event (from SessionTimer or axios interceptor)
    this.$root.$on('session-expired', () => {
      this.handleSessionExpiredEvent()
    })
    
    // Also listen for window event (from axios interceptor)
    this.sessionExpiredHandler = () => {
      this.handleSessionExpiredEvent()
    }
    window.addEventListener('session-expired', this.sessionExpiredHandler)
  },
  created() {
    this.setUserInfo();
    this.getServerInfo();
    this.$store.dispatch('info/fetchBranding');
  },
  beforeDestroy() {
    // Clean up event listeners
    this.$root.$off('session-expired')
    if (this.sessionExpiredHandler) {
      window.removeEventListener('session-expired', this.sessionExpiredHandler)
    }
  }
};
</script>

<style>
@import "./assets/tagsStyle.css";
@import "./assets/tooltip.css";

#app {
  font-family: "Avenir", Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
  color: #2c3e50;
  height: 100%;
  width: 100%;
  overflow-x: hidden;
  overflow-y: auto;
}
</style>
